import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import useSystemHook from "../../hooks/SystemHook";
import DesktopTrade from "./DesktopTrade";
import MobileTrade from "./MobileTrade";
import ArgusRoleModal, { ModalState } from "../ArgusRoleModal";
import TradeLinkedDialog from "./TradeLinkedDialog";
import { getUserPageContent } from "../../api/pageContentController";
import { AccountType } from "../../utils/constants";

const BANNER_URL =
  "https://argusweb.azureedge.net/argus-web/trade-show/2019/TR2019_060.jpg";

const benefits = [
  {
    title: "Better Pricing (Trade Prices)",
    description:
      "Access significantly lower wholesale trade prices, allowing you to increase your profit margins when reselling or servicing customers.",
    image:
      "https://argusweb.azureedge.net/argus-web/trade-show/2024/TR2024_029.jpg",
  },
  {
    title: "Dedicated Business Support",
    description:
      "Get priority service, dedicated account managers, and expert product advice tailored from sales reps for workshops and resellers.",
    image:
      "https://argusweb.azureedge.net/argus-web/trade-show/2022/TR2022_006.jpeg",
  },
  {
    title: "Credit Facilities & Flexible Payment",
    description:
      "Apply for business credit (subject to approval), enjoy longer payment terms, and simplify your cash flow — ideal for growing businesses.",
    image: "/images/yoco-photo.jpg",
  },
  {
    title: "Bulk Buying & Volume Discounts",
    description:
      "Additional discounts on large orders, helping you stock up efficiently and competitively.",
    image:
      "https://argusweb.azureedge.net/argus-web/trade-show/2019/TR2019_003.jpg",
  },
];

const customers = [
  { name: "AutoZone", image: "/images/autozone.png" },
  { name: "Electrosales", image: "/images/electrosales.png" },
  { name: "Midas", image: "/images/Midas-Logo.png" },
  { name: "Mica", image: "/images/mica-logo.png" },
  { name: "Makro", image: "/images/makro.png" },
  { name: "Takealot", image: "/images/Takealot_logo.png" },
  { name: "Amazon", image: "/images/amazon.png" },
  { name: "Amper Alles", image: "/images/AMPER-ALLES.png" },
  { name: "DIY Depot", image: "/images/diy-depot-logo.jpg" },
  { name: "Supa Quick", image: "/images/Supa_Quick.png" },
];

const steps = [
  {
    number: 1,
    title: "Registration",
    description:
      "Register on the Argus website to be able to browse and purchase online.",
  },
  {
    number: 2,
    title: "Application",
    description:
      "Fill out the online trade account application form to get started.",
  },
  {
    number: 3,
    title: "Approval",
    description:
      "Our team reviews your trade application and approves your account.",
  },
  {
    number: 4,
    title: "Ordering",
    description: "Start placing orders and enjoy exclusive trade prices.",
  },
];

const Trade = () => {
  const navigate = useNavigate();
  const { isMobile } = useSystemHook();
  const { isAuthenticated, loginWithRedirect } = useArgusAuth();

  const [pageContent, setPageContent] = useState(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [linkedDialog, setLinkedDialog] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        try {
          const res = await getUserPageContent();
          if (res?.success) setPageContent(res.data);
        } catch (_) {}
      })();
    }
  }, [isAuthenticated]);

  const handleNewTrade = async () => {
    if (!isAuthenticated) {
      await loginWithRedirect({
        screenHint: "signup",
        appState: { returnTo: "/trade-application" },
      });
      return;
    }

    const accountType = pageContent?.accountType;

    if (accountType === AccountType.Commercial) {
      setLinkedDialog(true);
    } else {
      navigate("/trade-application");
    }
  };

  const handleExistingTrade = async () => {
    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo: "/trade" },
      });
      return;
    }

    const accountType = pageContent?.accountType;

    if (accountType === AccountType.Commercial) {
      setLinkedDialog(true);
    } else {
      setRoleModalOpen(true);
    }
  };

  const data = {
    bannerUrl: BANNER_URL,
    benefits,
    customers,
    steps,
    onNewTrade: handleNewTrade,
    onExistingTrade: handleExistingTrade,
  };

  return (
    <>
      {isMobile ? <MobileTrade {...data} /> : <DesktopTrade {...data} />}

      {/* Retail → open ArgusRoleModal directly in Trade state */}
      {roleModalOpen && (
        <ArgusRoleModal
          open={roleModalOpen}
          initialState={ModalState.Trade}
          setClose={() => setRoleModalOpen(false)}
          refreshPage={() => window.location.reload()}
        />
      )}

      {/* Trade (Commercial) → already linked dialog */}
      <TradeLinkedDialog
        open={linkedDialog}
        onClose={() => setLinkedDialog(false)}
      />
    </>
  );
};

export default Trade;
